/**
 * Firebase service for AI Arena analytics data.
 * Handles persistent storage of tournament results, model ratings, and game records.
 */

import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  addDoc,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import {
  AIModelRatingDoc,
  AIGameDoc,
  AITournamentDoc,
  RatingSnapshot,
  StyleMetrics,
  WinCondition,
  BlunderFeedbackDoc,
} from './AIAnalyticsTypes';

const RATINGS_COLLECTION = 'ai_model_ratings';
const GAMES_COLLECTION = 'ai_games';
const TOURNAMENTS_COLLECTION = 'ai_tournaments';
const FEEDBACK_COLLECTION = 'ai_blunder_feedback';

/** Rating history snapshot interval (every N games) */
const RATING_SNAPSHOT_INTERVAL = 5;

export class AIArenaFirebaseService {

  // ---- Write Operations ----

  /**
   * Save a completed game to Firebase and update both model ratings.
   */
  static async saveGameResult(game: AIGameDoc): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, GAMES_COLLECTION), {
        ...game,
        timestamp: new Date(),
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Initialize or update a model's persistent rating after a game.
   */
  static async updateModelRating(
    variantId: string,
    displayName: string,
    model: string,
    timeFrame: string,
    newRating: number,
    eloChange: number,
    result: 'win' | 'loss' | 'draw',
    gameLength: number,
    blunders: number,
    brilliants: number,
    winCondition: WinCondition | null,
  ): Promise<void> {
    try {
      const docRef = doc(db, RATINGS_COLLECTION, variantId);
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const existing = snap.data() as AIModelRatingDoc;
        const newGames = existing.games + 1;
        const newWins = existing.wins + (result === 'win' ? 1 : 0);
        const newLosses = existing.losses + (result === 'loss' ? 1 : 0);
        const newDraws = existing.draws + (result === 'draw' ? 1 : 0);

        // Running averages
        const totalBlunders = existing.avgBlundersPerGame * existing.games + blunders;
        const totalBrilliants = existing.avgBrilliantsPerGame * existing.games + brilliants;
        const totalGameLength = existing.avgGameLength * existing.games + gameLength;

        // Style metrics update
        const style = { ...existing.styleMetrics };
        if (result === 'win' && winCondition) {
          const totalWins = newWins;
          const raceWins = style.raceWinRate * existing.wins + (winCondition === 'race' ? 1 : 0);
          const captureWins = style.captureWinRate * existing.wins + (winCondition === 'capture' ? 1 : 0);
          style.raceWinRate = totalWins > 0 ? raceWins / totalWins : 0;
          style.captureWinRate = totalWins > 0 ? captureWins / totalWins : 0;
        }

        // Rating history snapshot
        const ratingHistory = [...existing.ratingHistory];
        if (newGames % RATING_SNAPSHOT_INTERVAL === 0) {
          ratingHistory.push({ rating: newRating, timestamp: new Date(), gameCount: newGames });
        }

        await setDoc(docRef, {
          model,
          timeFrame,
          displayName,
          rating: newRating,
          ratingHistory,
          games: newGames,
          wins: newWins,
          losses: newLosses,
          draws: newDraws,
          drawRate: newDraws / newGames,
          avgGameLength: totalGameLength / newGames,
          avgBlundersPerGame: totalBlunders / newGames,
          avgBrilliantsPerGame: totalBrilliants / newGames,
          styleMetrics: style,
          updatedAt: new Date(),
        } as AIModelRatingDoc);
      } else {
        // First time — create doc
        const isWin = result === 'win';
        const style: StyleMetrics = {
          raceWinRate: isWin && winCondition === 'race' ? 1 : 0,
          captureWinRate: isWin && winCondition === 'capture' ? 1 : 0,
          earlyAggression: 0,
        };

        await setDoc(docRef, {
          model,
          timeFrame,
          displayName,
          rating: newRating,
          ratingHistory: [{ rating: newRating, timestamp: new Date(), gameCount: 1 }],
          games: 1,
          wins: result === 'win' ? 1 : 0,
          losses: result === 'loss' ? 1 : 0,
          draws: result === 'draw' ? 1 : 0,
          drawRate: result === 'draw' ? 1 : 0,
          avgGameLength: gameLength,
          avgBlundersPerGame: blunders,
          avgBrilliantsPerGame: brilliants,
          styleMetrics: style,
          updatedAt: new Date(),
        } as AIModelRatingDoc);
      }
    } catch (error) {
      // Non-fatal — don't throw, tournament can continue
    }
  }

  /**
   * Save tournament metadata.
   */
  static async saveTournament(tournament: AITournamentDoc): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, TOURNAMENTS_COLLECTION), {
        ...tournament,
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  // ---- Read Operations ----

  /**
   * Get all model ratings (for the all-time leaderboard).
   */
  static async getModelRatings(): Promise<(AIModelRatingDoc & { _docId: string })[]> {
    try {
      const snapshot = await getDocs(collection(db, RATINGS_COLLECTION));
      return snapshot.docs.map(d => ({ ...(d.data() as AIModelRatingDoc), _docId: d.id }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Get recent games, optionally filtered by variant.
   */
  static async getRecentGames(count: number = 50, variantId?: string): Promise<(AIGameDoc & { _id: string })[]> {
    try {
      if (variantId) {
        // Query without orderBy to avoid requiring composite indexes
        const lightQ = query(
          collection(db, GAMES_COLLECTION),
          where('lightVariant', '==', variantId),
        );
        const darkQ = query(
          collection(db, GAMES_COLLECTION),
          where('darkVariant', '==', variantId),
        );
        const [lightSnap, darkSnap] = await Promise.all([getDocs(lightQ), getDocs(darkQ)]);

        // Deduplicate: a self-play game (same variant on both sides) appears in both queries
        const seen = new Set<string>();
        const allGames: (AIGameDoc & { _id: string })[] = [];
        for (const d of [...lightSnap.docs, ...darkSnap.docs]) {
          if (!seen.has(d.id)) {
            seen.add(d.id);
            allGames.push({ ...(d.data() as AIGameDoc), _id: d.id });
          }
        }
        // Sort client-side by timestamp descending
        allGames.sort((a, b) => {
          const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return tb - ta;
        });
        return allGames.slice(0, count);
      }

      const q = query(
        collection(db, GAMES_COLLECTION),
        orderBy('timestamp', 'desc'),
        firestoreLimit(count)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...(d.data() as AIGameDoc), _id: d.id }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Get head-to-head record between two variants.
   */
  static async getHeadToHead(
    variantA: string,
    variantB: string
  ): Promise<{ aWins: number; bWins: number; draws: number; games: AIGameDoc[] }> {
    try {
      // Games where A is light, B is dark
      const q1 = query(
        collection(db, GAMES_COLLECTION),
        where('lightVariant', '==', variantA),
        where('darkVariant', '==', variantB)
      );
      // Games where B is light, A is dark
      const q2 = query(
        collection(db, GAMES_COLLECTION),
        where('lightVariant', '==', variantB),
        where('darkVariant', '==', variantA)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const games = [
        ...snap1.docs.map(d => d.data() as AIGameDoc),
        ...snap2.docs.map(d => d.data() as AIGameDoc),
      ];

      let aWins = 0, bWins = 0, draws = 0;
      for (const g of games) {
        if (g.winner === 'draw') { draws++; continue; }
        const aIsLight = g.lightVariant === variantA;
        const aWon = (aIsLight && g.winner === 'light') || (!aIsLight && g.winner === 'dark');
        if (aWon) aWins++; else bWins++;
      }

      return { aWins, bWins, draws, games };
    } catch (error) {
      return { aWins: 0, bWins: 0, draws: 0, games: [] };
    }
  }

  /**
   * Get notable moves (blunders or brilliants) for a specific variant.
   */
  static async getNotableMoves(
    variantId: string,
    type: 'blunder' | 'brilliant',
    count: number = 20
  ): Promise<AIGameDoc[]> {
    try {
      // Get recent games involving this variant that have notable moves
      const games = await this.getRecentGames(100, variantId);
      return games.filter(g =>
        g.notableMoves.some(m => m.type === type && (
          (g.lightVariant === variantId && m.side === 'light') ||
          (g.darkVariant === variantId && m.side === 'dark')
        ))
      ).slice(0, count);
    } catch (error) {
      return [];
    }
  }

  /**
   * Get recent games that contain blunders.
   */
  static async getGamesWithBlunders(count: number = 50): Promise<(AIGameDoc & { _id: string })[]> {
    try {
      // Fetch recent games and filter client-side for those with blunders
      const q = query(
        collection(db, GAMES_COLLECTION),
        orderBy('timestamp', 'desc'),
        firestoreLimit(200)
      );
      const snap = await getDocs(q);
      const games = snap.docs
        .map(d => ({ ...(d.data() as AIGameDoc), _id: d.id }))
        .filter(g => g.notableMoves && g.notableMoves.some(m => m.type === 'blunder'));
      return games.slice(0, count);
    } catch (error) {
      return [];
    }
  }

  /**
   * Save human feedback on a blunder.
   */
  static async saveBlunderFeedback(feedback: BlunderFeedbackDoc): Promise<void> {
    try {
      await addDoc(collection(db, FEEDBACK_COLLECTION), {
        ...feedback,
        timestamp: new Date(),
      });
    } catch (error) {
    }
  }

  /**
   * Get all blunder feedback (to mark already-reviewed blunders).
   */
  static async getBlunderFeedback(): Promise<BlunderFeedbackDoc[]> {
    try {
      const q = query(
        collection(db, FEEDBACK_COLLECTION),
        orderBy('timestamp', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data() as BlunderFeedbackDoc);
    } catch (error) {
      return [];
    }
  }

  /**
   * Get piece selection learning stats for an AI model.
   * Returns data from the ai_piece_stats collection (keyed by model, e.g. "sentinel").
   * Returns null if no data exists yet for this model.
   */
  static async getPieceSelectionStats(aiModel: string): Promise<any | null> {
    try {
      const docRef = doc(db, 'ai_piece_stats', aiModel);
      const snap = await getDoc(docRef);
      return snap.exists() ? snap.data() : null;
    } catch (error) {
      return null;
    }
  }

  // ---- Destructive Operations ----

  /**
   * Delete ALL arena data: games, ratings, tournaments, and feedback.
   * Used to wipe stale data when the schema changes.
   */
  static async purgeAllData(): Promise<{ games: number; ratings: number; tournaments: number; feedback: number }> {
    const counts = { games: 0, ratings: 0, tournaments: 0, feedback: 0 };
    const collections = [
      { name: GAMES_COLLECTION, key: 'games' as const },
      { name: RATINGS_COLLECTION, key: 'ratings' as const },
      { name: TOURNAMENTS_COLLECTION, key: 'tournaments' as const },
      { name: FEEDBACK_COLLECTION, key: 'feedback' as const },
    ];

    for (const col of collections) {
      try {
        const snap = await getDocs(collection(db, col.name));
        for (const d of snap.docs) {
          await deleteDoc(doc(db, col.name, d.id));
          counts[col.key]++;
        }
      } catch (error) {
      }
    }

    return counts;
  }

  /**
   * Consolidate all rating data into deep variants.
   * All pre-existing data was played at ~10-12s thinking time.
   * This reads ALL rating docs, groups by base model, picks the doc with the
   * most games as the "real" data, writes it to model-deep, and deletes the rest.
   * Returns the number of models consolidated.
   */
  static async migrateRatingsToDeepVariants(): Promise<number> {
    const MODELS = ['gaia', 'sentinel', 'alchemist', 'zenith', 'hunter', 'wisp', 'creature'];
    const SUFFIXES = ['', '-fast', '-standard', '-deep'];
    let consolidated = 0;

    try {
      for (const model of MODELS) {
        // Gather all docs for this model (bare name + all time suffixes)
        const allDocs: { id: string; data: AIModelRatingDoc }[] = [];

        for (const suffix of SUFFIXES) {
          const docId = `${model}${suffix}`;
          const ref = doc(db, RATINGS_COLLECTION, docId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            allDocs.push({ id: docId, data: snap.data() as AIModelRatingDoc });
          }
        }

        if (allDocs.length === 0) continue;

        // Find the doc with the most games — that's the real historical data
        const bestDoc = allDocs.reduce((best, curr) =>
          curr.data.games > best.data.games ? curr : best
        );

        // If the best doc has 0 games, nothing to consolidate
        if (bestDoc.data.games === 0) {
          // Just clean up non-deep docs
          for (const d of allDocs) {
            if (d.id !== `${model}-deep`) {
              await deleteDoc(doc(db, RATINGS_COLLECTION, d.id));
            }
          }
          continue;
        }

        // Write consolidated data to model-deep
        const deepId = `${model}-deep`;
        const deepRef = doc(db, RATINGS_COLLECTION, deepId);
        await setDoc(deepRef, {
          ...bestDoc.data,
          model,
          timeFrame: 'deep',
          updatedAt: new Date(),
        });

        // Delete all other docs for this model
        for (const d of allDocs) {
          if (d.id !== deepId) {
            await deleteDoc(doc(db, RATINGS_COLLECTION, d.id));
          }
        }

        consolidated++;
      }
    } catch (error) {
    }

    return consolidated;
  }

  /**
   * Delete only blunder feedback data.
   */
  static async purgeBlunderFeedback(): Promise<number> {
    let count = 0;
    try {
      const snap = await getDocs(collection(db, FEEDBACK_COLLECTION));
      for (const d of snap.docs) {
        await deleteDoc(doc(db, FEEDBACK_COLLECTION, d.id));
        count++;
      }
    } catch (error) {
    }
    return count;
  }
}
